import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface AsyncSelectDropdownProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  panelRef: React.RefObject<HTMLDivElement | null>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  options: any[];
  isLoading: boolean;
  hasMore: boolean;
  value?: string;
  getOptionLabel: (item: any) => string;
  getOptionValue: (item: any) => string;
  onSelect: (item: any) => void;
  onLoadMore: () => void;
}

/**
 * Dropdown panel rendered in a portal at document.body so it always paints
 * above page content (theme backdrop-blur creates stacking contexts that
 * trap absolutely-positioned children behind sibling sections).
 */
const AsyncSelectDropdown: React.FC<AsyncSelectDropdownProps> = ({
  anchorRef,
  panelRef,
  searchInputRef,
  searchQuery,
  onSearchChange,
  options,
  isLoading,
  hasMore,
  value,
  getOptionLabel,
  getOptionValue,
  onSelect,
  onLoadMore,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition({ top: rect.bottom + 8, left: rect.left, width: rect.width });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    // capture-phase scroll so scrolling any ancestor repositions the panel
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  return createPortal(
    <div
      ref={panelRef}
      style={{ position: 'fixed', top: position.top, left: position.left, width: position.width, zIndex: 9999 }}
      className="bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden flex flex-col"
    >
      <div className="p-3 border-b border-gray-100 bg-gray-50/70 rounded-t-xl">
        <div className="relative">
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-ocean-500/30 focus:border-ocean-500"
          />
        </div>
      </div>

      <div className="overflow-y-auto flex-1 py-1">
        {isLoading && options.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading...</div>
        ) : options.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">No options found</div>
        ) : (
          <>
            {options.map((item, index) => {
              const optionValue = getOptionValue(item);
              const optionLabel = getOptionLabel(item);
              const isSelected = optionValue === value;

              return (
                <button
                  key={`${optionValue}-${index}`}
                  type="button"
                  onClick={() => onSelect(item)}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-ocean-50 ${
                    isSelected ? 'bg-ocean-100 text-ocean-900 font-medium' : 'text-gray-800'
                  }`}
                >
                  {optionLabel}
                </button>
              );
            })}

            {hasMore && (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoading}
                className="w-full px-4 py-2.5 text-center text-sm font-medium text-ocean-600 hover:bg-ocean-50 border-t border-gray-100 disabled:text-gray-400"
              >
                {isLoading ? 'Loading...' : 'Load more...'}
              </button>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AsyncSelectDropdown;
